/**
 * CenikyWorkspace — Phase 10.2 ceník editor.
 *
 * Entities, colours, interiors and equipment in one workspace under
 * `/management?tab=ceniky`. Layout:
 *
 *   Toolband — Brand select · Model select · "Nová entita" button
 *   SegmentedControl — Entity · Barvy · Interiéry · Výbavy
 *   For "Entity": DataTable of entities for the selected model.
 *   For items (Barvy / Interiéry / Výbavy): top DataTable of entities, then a
 *     second DataTable of items for the picked entity.
 *
 * Edit/create flow: clicking the row's edit / + button opens a Modal with a
 * form (RHF wasn't needed here — fields are <8 and a single submit). Saving
 * issues POST against the matching `/v1/management/{entities,colors,
 * interiors,equipment}` endpoint; deleting issues DELETE. List queries are
 * invalidated on every mutation so the table refreshes without a manual
 * reload.
 *
 * Editing an entity is structurally a "replace" — the API deactivates the
 * old row and writes a new one, mirroring the legacy semantics. Items
 * (barvy/interiéry/výbavy) follow the same replace-on-edit pattern via
 * their respective `id_*` body fields.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { Field, Fields } from '@/components/Field';
import { Icon } from '@/components/Icon';
import { Modal, DialogCrown } from '@/components/Modal';
import { SegmentedControl } from '@/components/SegmentedControl';
import { Skeleton } from '@/components/Skeleton';
import { TextInput } from '@/components/TextInput';
import { Toolband } from '@/components/Toolband';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { formatInt } from '@/lib/format';
import {
  useCenikImportExtractMutation,
  useCreateColorMutation,
  useCreateEntityMutation,
  useCreateEquipmentMutation,
  useCreateInteriorMutation,
  useDeleteColorMutation,
  useDeleteEntityMutation,
  useDeleteEquipmentMutation,
  useDeleteInteriorMutation,
  useMgmtColorsQuery,
  useMgmtEntitiesQuery,
  useMgmtEquipmentQuery,
  useMgmtInteriorsQuery,
  useMgmtModelsQuery,
  type CenikImportDraft,
  type CenikImportExtractProgress,
  type MgmtBrand,
  type MgmtEntity,
  type MgmtItem,
  type MgmtItemBody,
} from '@/modules/management/api';
import { CenikImportReview } from './CenikImportReview';

type Category = 'entity' | 'barvy' | 'interiery' | 'vybavy';

const CATEGORY_OPTIONS = [
  { value: 'entity', label: 'Entity' },
  { value: 'barvy', label: 'Barvy' },
  { value: 'interiery', label: 'Interiéry' },
  { value: 'vybavy', label: 'Výbavy' },
] as const;

interface CenikyWorkspaceProps {
  brands: MgmtBrand[];
  brandsLoading: boolean;
}

export function CenikyWorkspace({ brands, brandsLoading }: CenikyWorkspaceProps) {
  const [brandId, setBrandId] = useState<number | null>(null);
  const [modelId, setModelId] = useState<number | null>(null);
  const [entityId, setEntityId] = useState<number | null>(null);
  const [category, setCategory] = useState<Category>('entity');
  const [entityModal, setEntityModal] = useState<{ mode: 'create' | 'edit'; entity?: MgmtEntity } | null>(null);
  const [itemModal, setItemModal] = useState<{ mode: 'create' | 'edit'; item?: MgmtItem } | null>(null);

  // Nissan XLSX import — extraction progress + the draft under review. While
  // a draft is open the workspace body swaps to the review screen.
  const importInput = useRef<HTMLInputElement>(null);
  const extract = useCenikImportExtractMutation();
  const [importDraft, setImportDraft] = useState<CenikImportDraft | null>(null);
  const [importStav, setImportStav] = useState<
    (CenikImportExtractProgress & { od: number }) | null
  >(null);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!importStav) return;
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only presence matters
  }, [importStav != null]);

  const runImport = (file: File) => {
    setImportStav({ faze: 'ulozeno', nazev: null, poradi: 0, celkem: 0, pocetRadku: 0, od: Date.now() });
    extract.mutate(
      { file, onProgress: (p) => setImportStav((prev) => ({ ...p, od: prev?.od ?? Date.now() })) },
      {
        onSettled: () => {
          setImportStav(null);
          if (importInput.current) importInput.current.value = '';
        },
        onSuccess: (r) => {
          if (r.draft) {
            setImportDraft(r.draft);
            toast.ok(
              `Ceník vytěžen — ${formatInt(r.draft.modely.reduce((n, m) => n + m.entity.length, 0))} entit ke kontrole.`,
            );
            if (r.draft.chyby.length > 0) {
              toast.warn(`${formatInt(r.draft.chyby.length)} listů se nepodařilo vytěžit — viz kontrola.`);
            }
          } else {
            toast.warn(
              r.chyba ?? 'AI extrakce není nakonfigurována (ANTHROPIC_API_KEY) — sešit je uložen.',
            );
          }
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Import ceníku selhal'),
      },
    );
  };

  // Default-select first brand / model once the lists land.
  useEffect(() => {
    if (brandId == null && brands.length > 0) {
      setBrandId(brands[0]!.id);
    }
  }, [brandId, brands]);

  const models = useMgmtModelsQuery(brandId);
  useEffect(() => {
    if (modelId == null && models.data && models.data.models.length > 0) {
      setModelId(models.data.models[0]!.id);
    }
    if (modelId != null && models.data && !models.data.models.some((m) => m.id === modelId)) {
      // Brand changed underneath; reset model.
      setModelId(models.data.models[0]?.id ?? null);
      setEntityId(null);
    }
  }, [modelId, models.data]);

  const entities = useMgmtEntitiesQuery(modelId);
  useEffect(() => {
    if (entityId != null && entities.data && !entities.data.entities.some((e) => e.id === entityId)) {
      setEntityId(null);
    }
  }, [entityId, entities.data]);

  const isNissan = brands.find((b) => b.id === brandId)?.nazev === 'Nissan';
  const reviewOpen = importDraft != null;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Toolband>
        <div className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Značka
        </div>
        <select
          aria-label="Značka"
          value={brandId ?? ''}
          disabled={brandsLoading || brands.length === 0 || reviewOpen}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            setBrandId(Number.isFinite(v) ? v : null);
            setModelId(null);
            setEntityId(null);
          }}
          className="min-w-[180px] border border-line bg-paper px-2.5 py-1.5 font-mono text-[12px] text-ink focus:border-accent focus:outline-none"
        >
          {brands.length === 0 && <option>—</option>}
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.nazev}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          Model
        </div>
        <select
          aria-label="Model"
          value={modelId ?? ''}
          disabled={!models.data || models.data.models.length === 0 || reviewOpen}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            setModelId(Number.isFinite(v) ? v : null);
            setEntityId(null);
          }}
          className="min-w-[180px] border border-line bg-paper px-2.5 py-1.5 font-mono text-[12px] text-ink focus:border-accent focus:outline-none disabled:opacity-50"
        >
          {(!models.data || models.data.models.length === 0) && <option>—</option>}
          {models.data?.models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nazev_modelu}
            </option>
          ))}
        </select>

        <SegmentedControl<Category>
          aria-label="Kategorie ceníku"
          options={CATEGORY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          value={category}
          onChange={(v) => {
            setCategory(v);
            setEntityId(null);
          }}
        />

        <Toolband.Spacer />

        {isNissan && (
          <>
            <input
              ref={importInput}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) runImport(f);
              }}
            />
            <Button
              variant="secondary"
              size="sm"
              icon={<Icon name="upload" size={12} />}
              onClick={() => importInput.current?.click()}
              disabled={extract.isPending || reviewOpen}
            >
              {extract.isPending ? 'Vytěžuji…' : 'Import ceníku (XLSX)'}
            </Button>
          </>
        )}

        <Button
          variant="primary"
          size="sm"
          icon={<Icon name="plus" size={12} />}
          disabled={modelId == null || reviewOpen}
          onClick={() => {
            if (category === 'entity') {
              setEntityModal({ mode: 'create' });
            } else if (entityId != null) {
              setItemModal({ mode: 'create' });
            }
          }}
        >
          {category === 'entity' ? 'Nová entita' : `Nová položka`}
        </Button>
      </Toolband>

      <div
        data-testid="management-ceniky"
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5"
      >
        {importStav && (
          <div
            data-testid="cenik-import-progress"
            className="flex items-center gap-3 border border-line bg-paper px-4 py-3"
          >
            <span className="inline-block h-2 w-2 animate-pulse bg-accent" aria-hidden />
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-2">
              {importStav.faze === 'ulozeno' && 'Ukládám sešit a připravuji AI…'}
              {importStav.faze === 'cteni' &&
                `List ${importStav.nazev ?? '…'} (${importStav.poradi}/${importStav.celkem}) — AI čte…`}
              {importStav.faze === 'radky' &&
                `List ${importStav.nazev ?? '…'} (${importStav.poradi}/${importStav.celkem}) — vytěženo ${formatInt(importStav.pocetRadku)} entit`}
            </span>
            <span className="ml-auto font-mono text-[11px] tabular-nums text-ink-3">
              {(() => {
                const s = Math.floor((Date.now() - importStav.od) / 1000);
                return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
              })()}
            </span>
          </div>
        )}
        {importDraft != null && brandId != null ? (
          <CenikImportReview
            draft={importDraft}
            brandId={brandId}
            onClose={() => setImportDraft(null)}
          />
        ) : modelId == null ? (
          <EmptyState
            eyebrow="Vyberte model"
            title="Začněte výběrem značky a modelu vozidla v horní liště."
            description="Ceníky jsou cílené na konkrétní entity, které vznikají nad zvoleným modelem."
          />
        ) : entities.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : entities.data?.entities.length === 0 ? (
          <EmptyState
            eyebrow="Prázdné"
            title="K tomuto modelu zatím nejsou žádné entity."
            description="Vytvořte první entitu tlačítkem „Nová entita“ vpravo nahoře."
          />
        ) : (
          <>
            <EntityTable
              entities={entities.data?.entities ?? []}
              selectedId={category === 'entity' ? null : entityId}
              onEdit={(entity) =>
                category === 'entity'
                  ? setEntityModal({ mode: 'edit', entity })
                  : setEntityId(entity.id)
              }
              category={category}
            />

            {category !== 'entity' && entityId != null && (
              <ItemsForEntity
                category={category}
                entityId={entityId}
                onEdit={(item) => setItemModal({ mode: 'edit', item })}
                onNew={() => setItemModal({ mode: 'create' })}
              />
            )}
          </>
        )}
      </div>

      <EntityFormModal
        open={entityModal != null}
        modelId={modelId}
        mode={entityModal?.mode ?? 'create'}
        entity={entityModal?.entity}
        onOpenChange={(open) => {
          if (!open) setEntityModal(null);
        }}
      />

      <ItemFormModal
        open={itemModal != null}
        category={category === 'entity' ? 'barvy' : category}
        entityId={entityId}
        mode={itemModal?.mode ?? 'create'}
        item={itemModal?.item}
        onOpenChange={(open) => {
          if (!open) setItemModal(null);
        }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Entity table
   ───────────────────────────────────────────────────────────────────────── */

interface EntityTableProps {
  entities: MgmtEntity[];
  selectedId: number | null;
  onEdit: (entity: MgmtEntity) => void;
  category: Category;
}

function EntityTable({ entities, selectedId, onEdit, category }: EntityTableProps) {
  const cols = useMemo<DataTableColumn<MgmtEntity>[]>(() => {
    return [
      {
        id: 'provedeni',
        priority: 'primary',
        header: 'Provedení',
        cell: (e) => <span className="truncate text-ink">{e.provedeni || '—'}</span>,
        width: 'minmax(0, 1fr)',
      },
      {
        id: 'verze',
        priority: 'secondary',
        header: 'Verze',
        cell: (e) => <span className="truncate text-ink-2">{e.verze || '—'}</span>,
        width: 'minmax(0, 1fr)',
      },
      {
        id: 'motor',
        priority: 'secondary',
        header: 'Motor',
        cell: (e) => <span className="truncate text-ink-2">{e.motor || '—'}</span>,
        width: 'minmax(0, 1fr)',
      },
      {
        id: 'prevodovka',
        priority: 'secondary',
        header: 'Převodovka',
        cell: (e) => <span className="truncate text-ink-2">{e.prevodovka || '—'}</span>,
        width: 'minmax(0, 0.7fr)',
      },
      {
        id: 'cenikova',
        priority: 'meta',
        header: 'Ceník bez DPH',
        cell: (e) => (
          <span className="font-mono text-[11.5px] tabular-nums text-ink">
            {formatInt(e.cenikova_cena)} Kč
          </span>
        ),
        width: '140px',
        align: 'right',
      },
      {
        id: 'nakupni',
        priority: 'meta',
        header: 'Nákup bez DPH',
        cell: (e) => (
          <span className="font-mono text-[11.5px] tabular-nums text-ink-2">
            {formatInt(e.nakupni_cena)} Kč
          </span>
        ),
        width: '140px',
        align: 'right',
      },
      {
        id: 'action',
        header: '',
        cell: () => (
          <Icon
            name={category === 'entity' ? 'edit' : 'check'}
            size={14}
            className="text-ink-3 group-hover:text-ink"
          />
        ),
        width: '40px',
        align: 'center',
      },
    ];
  }, [category]);

  return (
    <DataTable<MgmtEntity>
      aria-label="Entity"
      variant="bordered"
      columns={cols}
      data={entities}
      getRowId={(e) => String(e.id)}
      selectedId={selectedId != null ? String(selectedId) : undefined}
      onRowClick={(entity) => onEdit(entity)}
      empty={<EmptyState eyebrow="Prázdné" title="Žádné entity" />}
    />
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Items for entity (Barvy / Interiéry / Výbavy)
   ───────────────────────────────────────────────────────────────────────── */

interface ItemsForEntityProps {
  category: Exclude<Category, 'entity'>;
  entityId: number;
  onEdit: (item: MgmtItem) => void;
  onNew: () => void;
}

function ItemsForEntity({ category, entityId, onEdit, onNew }: ItemsForEntityProps) {
  const colors = useMgmtColorsQuery(category === 'barvy' ? entityId : null);
  const interiors = useMgmtInteriorsQuery(category === 'interiery' ? entityId : null);
  const equipment = useMgmtEquipmentQuery(category === 'vybavy' ? entityId : null);

  const query =
    category === 'barvy' ? colors : category === 'interiery' ? interiors : equipment;

  const items: MgmtItem[] =
    category === 'barvy'
      ? colors.data?.colors ?? []
      : category === 'interiery'
        ? interiors.data?.interiors ?? []
        : equipment.data?.equipment ?? [];

  const heading =
    category === 'barvy'
      ? 'Barvy'
      : category === 'interiery'
        ? 'Interiéry'
        : 'Výbavy';

  const cols = useMemo<DataTableColumn<MgmtItem>[]>(
    () => [
      {
        id: 'nazev',
        priority: 'primary',
        header: 'Název',
        cell: (i) => <span className="truncate text-ink">{i.nazev || '—'}</span>,
        width: 'minmax(0, 1fr)',
      },
      {
        id: 'cenikova',
        priority: 'meta',
        header: 'Ceník bez DPH',
        cell: (i) => (
          <span className="font-mono text-[11.5px] tabular-nums text-ink">
            {formatInt(i.cenikova_cena)} Kč
          </span>
        ),
        width: '140px',
        align: 'right',
      },
      {
        id: 'nakupni',
        priority: 'meta',
        header: 'Nákup bez DPH',
        cell: (i) => (
          <span className="font-mono text-[11.5px] tabular-nums text-ink-2">
            {formatInt(i.nakupni_cena)} Kč
          </span>
        ),
        width: '140px',
        align: 'right',
      },
      {
        id: 'action',
        header: '',
        cell: () => <Icon name="edit" size={14} className="text-ink-3 group-hover:text-ink" />,
        width: '40px',
        align: 'center',
      },
    ],
    [],
  );

  return (
    <section className="flex flex-col gap-3">
      <header className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3">
        <span aria-hidden className="inline-block h-px w-3.5 bg-accent" />
        <span>{heading}</span>
        <span className="text-[10px] text-ink-3">·</span>
        <span className="text-[10px] text-ink-3">{formatInt(items.length)} položek</span>
        <span className="ml-auto" />
        <Button
          size="sm"
          icon={<Icon name="plus" size={12} />}
          onClick={onNew}
        >
          Nová položka
        </Button>
      </header>
      {query.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : items.length === 0 ? (
        <EmptyState eyebrow="Prázdné" title={`Žádné záznamy v sekci ${heading.toLowerCase()}`} />
      ) : (
        <DataTable<MgmtItem>
          aria-label={heading}
          variant="bordered"
          columns={cols}
          data={items}
          getRowId={(i) => String(i.id)}
          onRowClick={onEdit}
        />
      )}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Entity create / edit modal
   ───────────────────────────────────────────────────────────────────────── */

interface EntityFormModalProps {
  open: boolean;
  modelId: number | null;
  mode: 'create' | 'edit';
  entity?: MgmtEntity;
  onOpenChange: (open: boolean) => void;
}

interface EntityFormState {
  provedeni: string;
  verze: string;
  motor: string;
  prevodovka: string;
  cenikova_cena: string;
  nakupni_cena: string;
}

function EntityFormModal({
  open,
  modelId,
  mode,
  entity,
  onOpenChange,
}: EntityFormModalProps) {
  const create = useCreateEntityMutation();
  const remove = useDeleteEntityMutation();
  const [form, setForm] = useState<EntityFormState>(emptyEntity());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && entity) {
      setForm({
        provedeni: entity.provedeni,
        verze: entity.verze,
        motor: entity.motor,
        prevodovka: entity.prevodovka,
        cenikova_cena: String(entity.cenikova_cena ?? 0),
        nakupni_cena: String(entity.nakupni_cena ?? 0),
      });
    } else {
      setForm(emptyEntity());
    }
    setConfirmingDelete(false);
    queueMicrotask(() => firstFieldRef.current?.focus());
  }, [open, mode, entity]);

  const submit = () => {
    if (modelId == null) return;
    const cenikova = parseCzNumber(form.cenikova_cena);
    const nakupni = parseCzNumber(form.nakupni_cena);
    if (!Number.isFinite(cenikova) || !Number.isFinite(nakupni)) {
      toast.error('Zadejte platnou číselnou hodnotu cen.');
      return;
    }
    create.mutate(
      {
        vozidlo_model: modelId,
        provedeni: form.provedeni.trim(),
        verze: form.verze.trim(),
        motor: form.motor.trim(),
        prevodovka: form.prevodovka.trim(),
        cenikova_cena: cenikova,
        nakupni_cena: nakupni,
        id_entity: mode === 'edit' && entity ? entity.id : undefined,
      },
      {
        onSuccess: () => {
          toast.ok(mode === 'edit' ? 'Entita aktualizována' : 'Entita vytvořena');
          onOpenChange(false);
        },
        onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se uložit.')),
      },
    );
  };

  const onDelete = () => {
    if (!entity) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    remove.mutate(entity.id, {
      onSuccess: () => {
        toast.ok('Entita smazána');
        onOpenChange(false);
      },
      onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se smazat.')),
    });
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth={680}>
      <DialogCrown
        tone="neutral"
        eyebrow={mode === 'edit' ? 'Úprava entity' : 'Nová entita'}
        eyebrowMeta={mode === 'edit' && entity ? `#${entity.id}` : undefined}
        title={
          mode === 'edit' ? (
            <>
              Upravit <em>entitu</em>.
            </>
          ) : (
            <>
              Vytvořit <em>novou entitu</em>.
            </>
          )
        }
      />
      <Modal.Body className="px-8 py-6">
        <Fields cols={2}>
          <Field label="Provedení">
            <TextInput
              ref={firstFieldRef}
              value={form.provedeni}
              onChange={(e) => setForm({ ...form, provedeni: e.target.value })}
            />
          </Field>
          <Field label="Verze">
            <TextInput
              value={form.verze}
              onChange={(e) => setForm({ ...form, verze: e.target.value })}
            />
          </Field>
          <Field label="Motor">
            <TextInput
              value={form.motor}
              onChange={(e) => setForm({ ...form, motor: e.target.value })}
            />
          </Field>
          <Field label="Převodovka">
            <TextInput
              value={form.prevodovka}
              onChange={(e) => setForm({ ...form, prevodovka: e.target.value })}
            />
          </Field>
          <Field label="Ceníková cena bez DPH">
            <TextInput
              type="number"
              value={form.cenikova_cena}
              suffix="Kč"
              onChange={(e) => setForm({ ...form, cenikova_cena: e.target.value })}
            />
          </Field>
          <Field label="Nákupní cena bez DPH">
            <TextInput
              type="number"
              value={form.nakupni_cena}
              suffix="Kč"
              onChange={(e) => setForm({ ...form, nakupni_cena: e.target.value })}
            />
          </Field>
        </Fields>
      </Modal.Body>
      <Modal.Foot
        meta={
          mode === 'edit' ? (
            <span>
              <b>Uložením</b> vznikne nová verze, předchozí zůstane jako historie.
            </span>
          ) : (
            'Esc · zavřít'
          )
        }
        actions={
          <>
            {mode === 'edit' && (
              <Button
                variant="secondary"
                size="md"
                onClick={onDelete}
                disabled={remove.isPending}
              >
                {confirmingDelete ? 'Opravdu smazat?' : 'Smazat'}
              </Button>
            )}
            <Button
              variant="secondary"
              size="md"
              onClick={() => onOpenChange(false)}
            >
              Zrušit
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={submit}
              disabled={create.isPending}
            >
              {create.isPending ? 'Ukládám…' : mode === 'edit' ? 'Uložit verzi' : 'Vytvořit'}
            </Button>
          </>
        }
      />
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Item create / edit modal (Barvy / Interiéry / Výbavy)
   ───────────────────────────────────────────────────────────────────────── */

interface ItemFormModalProps {
  open: boolean;
  category: Exclude<Category, 'entity'>;
  entityId: number | null;
  mode: 'create' | 'edit';
  item?: MgmtItem;
  onOpenChange: (open: boolean) => void;
}

interface ItemFormState {
  nazev: string;
  cenikova_cena: string;
  nakupni_cena: string;
}

function ItemFormModal({
  open,
  category,
  entityId,
  mode,
  item,
  onOpenChange,
}: ItemFormModalProps) {
  const createColor = useCreateColorMutation();
  const deleteColor = useDeleteColorMutation();
  const createInterior = useCreateInteriorMutation();
  const deleteInterior = useDeleteInteriorMutation();
  const createEquipment = useCreateEquipmentMutation();
  const deleteEquipment = useDeleteEquipmentMutation();

  const create =
    category === 'barvy'
      ? createColor
      : category === 'interiery'
        ? createInterior
        : createEquipment;
  const remove =
    category === 'barvy'
      ? deleteColor
      : category === 'interiery'
        ? deleteInterior
        : deleteEquipment;

  const heading =
    category === 'barvy' ? 'barvy' : category === 'interiery' ? 'interiéru' : 'výbavy';

  const [form, setForm] = useState<ItemFormState>(emptyItem());
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && item) {
      setForm({
        nazev: item.nazev,
        cenikova_cena: String(item.cenikova_cena ?? 0),
        nakupni_cena: String(item.nakupni_cena ?? 0),
      });
    } else {
      setForm(emptyItem());
    }
    setConfirmingDelete(false);
    queueMicrotask(() => firstFieldRef.current?.focus());
  }, [open, mode, item]);

  const submit = () => {
    if (entityId == null) return;
    const nazev = form.nazev.trim();
    if (!nazev) {
      toast.error('Zadejte název.');
      return;
    }
    const cenikova = parseCzNumber(form.cenikova_cena);
    const nakupni = parseCzNumber(form.nakupni_cena);
    if (!Number.isFinite(cenikova) || !Number.isFinite(nakupni)) {
      toast.error('Zadejte platnou číselnou hodnotu cen.');
      return;
    }
    const body: MgmtItemBody = {
      id_entity: entityId,
      nazev,
      cenikova_cena: cenikova,
      nakupni_cena: nakupni,
    };
    if (mode === 'edit' && item) {
      if (category === 'barvy') body.id_barvy = item.id;
      if (category === 'interiery') body.id_interieru = item.id;
      if (category === 'vybavy') body.id_vybavy = item.id;
    }
    create.mutate(body, {
      onSuccess: () => {
        toast.ok(
          mode === 'edit'
            ? `${capitalize(heading)} aktualizována`
            : `${capitalize(heading)} vytvořena`,
        );
        onOpenChange(false);
      },
      onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se uložit.')),
    });
  };

  const onDelete = () => {
    if (!item) return;
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    remove.mutate(item.id, {
      onSuccess: () => {
        toast.ok('Položka smazána');
        onOpenChange(false);
      },
      onError: (err) => toast.error(apiMessage(err, 'Nepodařilo se smazat.')),
    });
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} maxWidth={560}>
      <DialogCrown
        tone="neutral"
        eyebrow={mode === 'edit' ? `Úprava ${heading}` : `Nová ${heading}`}
        eyebrowMeta={mode === 'edit' && item ? `#${item.id}` : undefined}
        title={
          mode === 'edit' ? (
            <>
              Upravit <em>{heading}</em>.
            </>
          ) : (
            <>
              Přidat <em>{heading}</em>.
            </>
          )
        }
      />
      <Modal.Body className="px-8 py-6">
        <Fields cols={1}>
          <Field label="Název">
            <TextInput
              ref={firstFieldRef}
              value={form.nazev}
              onChange={(e) => setForm({ ...form, nazev: e.target.value })}
            />
          </Field>
        </Fields>
        <div className="mt-4">
          <Fields cols={2}>
            <Field label="Ceníková cena bez DPH">
              <TextInput
                type="number"
                value={form.cenikova_cena}
                suffix="Kč"
                onChange={(e) => setForm({ ...form, cenikova_cena: e.target.value })}
              />
            </Field>
            <Field label="Nákupní cena bez DPH">
              <TextInput
                type="number"
                value={form.nakupni_cena}
                suffix="Kč"
                onChange={(e) => setForm({ ...form, nakupni_cena: e.target.value })}
              />
            </Field>
          </Fields>
        </div>
      </Modal.Body>
      <Modal.Foot
        meta={
          mode === 'edit' ? (
            <span>
              <b>Uložením</b> vznikne nová verze položky.
            </span>
          ) : (
            'Esc · zavřít'
          )
        }
        actions={
          <>
            {mode === 'edit' && (
              <Button
                variant="secondary"
                size="md"
                onClick={onDelete}
                disabled={remove.isPending}
              >
                {confirmingDelete ? 'Opravdu smazat?' : 'Smazat'}
              </Button>
            )}
            <Button
              variant="secondary"
              size="md"
              onClick={() => onOpenChange(false)}
            >
              Zrušit
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={submit}
              disabled={create.isPending}
            >
              {create.isPending ? 'Ukládám…' : mode === 'edit' ? 'Uložit verzi' : 'Vytvořit'}
            </Button>
          </>
        }
      />
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Helpers
   ───────────────────────────────────────────────────────────────────────── */

function emptyEntity(): EntityFormState {
  return {
    provedeni: '',
    verze: '',
    motor: '',
    prevodovka: '',
    cenikova_cena: '0',
    nakupni_cena: '0',
  };
}

function emptyItem(): ItemFormState {
  return {
    nazev: '',
    cenikova_cena: '0',
    nakupni_cena: '0',
  };
}

function parseCzNumber(input: string): number {
  if (input == null) return NaN;
  const cleaned = input.replace(/\s/g, '').replace(',', '.');
  return Number.parseFloat(cleaned);
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}

function apiMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const e = (err.body as { error?: string }).error;
    if (typeof e === 'string') return e;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
